import { useRef, useEffect, useState } from "react";
import {
  Card,
  Button,
  Switch,
  DatePicker,
  Modal,
  Typography,
  Space,
  Tooltip,
} from "antd";
import {
  EditFilled,
  FilePdfOutlined,
  CopyOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import logo from "../../img/logo_white.png";
import html2pdf from "html2pdf.js";
import {
  getPitanjaSaOdgovorima,
  getById,
  updateAnketa,
} from "../../services/anketa.service";
import dayjs from "dayjs";
import axios from "axios";
import "./AnketaSettings.css";

const { Title, Text } = Typography;

const AnketaSettings = ({ anketaId }) => {
  const [anketa, setAnketa] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [dateConfirmVisible, setDateConfirmVisible] = useState(false);
  const [token, setToken] = useState("");
  const [newStatus, setNewStatus] = useState(null);
  const [newDatumVazenja, setNewDatumVazenja] = useState(null);
  const navigate = useNavigate();
  const tokenRef = useRef(null);

  const handleStatusToggle = (checked) => {
    const status = checked ? "AKTIVNA" : "NEAKTIVNA";
    setNewStatus(status);
    setConfirmVisible(true);
  };

  const handleDateChange = (date) => {
    if (!date) return;
    setNewDatumVazenja(date);
    setDateConfirmVisible(true);
  };

  const confirmDateChange = async () => {
    try {
      const newDate = newDatumVazenja.format("YYYY-MM-DDTHH:mm:ss");
      const updated = { ...anketa, datumVazenja: newDate };
      await updateAnketa(anketa.id, updated);
      setAnketa(updated);
      alert("Datum važenja je ažuriran!");
    } catch (error) {
      alert("Greška pri ažuriranju datuma");
    } finally {
      setDateConfirmVisible(false);
    }
  };

  const cancelDateChange = () => {
    setDateConfirmVisible(false);
  };

  const confirmStatusChange = async () => {
    try {
      setLoading(true);
      const updated = { ...anketa, statusAnkete: newStatus };
      await updateAnketa(anketa.id, updated);
      setAnketa(updated);
      alert("Status je ažuriran!");
    } catch (error) {
      alert("Greška pri ažuriranju statusa.");
    } finally {
      setLoading(false);
      setConfirmVisible(false);
    }
  };

  const onUpdateClick = (id) => {
    navigate(`/zaposleni/anketa/${id}/edit`);
  };

  useEffect(() => {
    const fetchAnketa = async () => {
      try {
        setLoading(true);
        const response = await getById(anketaId);
        setAnketa(response.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchAnketa();
  }, [anketaId]);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const response = await axios.get(`/anketa/${anketaId}/token`);
        setToken(response.data);
      } catch (error) {}
    };
    if (anketaId) fetchToken();
  }, [anketaId]);

  const handleExportPDF = async (anketaId) => {
    try {
      const anketaResponse = await getById(anketaId);
      const pitanjaResponse = await getPitanjaSaOdgovorima(anketaId);

      const anketa = anketaResponse.data;
      const pitanja = pitanjaResponse.data;
      if (!anketa || !pitanja) return;

      const container = document.createElement("div");
      container.style.fontFamily = "Arial, sans-serif";
      container.style.padding = "40px";
      container.style.maxWidth = "600px";
      container.style.margin = "0 auto";

      const headerWrapper = document.createElement("div");
      headerWrapper.style.display = "flex";
      headerWrapper.style.justifyContent = "center";
      headerWrapper.style.alignItems = "center";
      headerWrapper.style.marginBottom = "40px";

      const logoImg = document.createElement("img");
      logoImg.src = logo;
      logoImg.style.width = "60px";
      logoImg.style.height = "60px";
      logoImg.style.objectFit = "contain";
      logoImg.style.marginRight = "0px";
      logoImg.style.marginTop = "-30px";
      const headerText = document.createElement("div");
      const orgName = document.createElement("h3");
      orgName.textContent = "Psihološko savjetovalište u Banjoj Luci";
      orgName.style.margin = "10";
      orgName.style.fontSize = "15px";
      orgName.style.color = "#2b494b";
      orgName.style.textAlign = "center";
      orgName.style.margin = "0 auto 20px";
      headerText.appendChild(orgName);
      headerWrapper.appendChild(logoImg);
      headerWrapper.appendChild(headerText);
      container.appendChild(headerWrapper);

      const title = document.createElement("h1");
      title.textContent = anketa.naziv;
      title.style.textAlign = "center";
      title.style.fontSize = "22px";
      title.style.display = "block";
      title.style.width = "100%";
      title.style.textAlign = "center";
      title.style.margin = "0 auto 30px";
      title.style.whiteSpace = "normal";
      title.style.wordWrap = "break-word";
      title.style.lineHeight = "1.4";
      container.appendChild(title);

      if (anketa.opis) {
        const opis = document.createElement("p");
        opis.textContent = anketa.opis;
        opis.style.textAlign = "center";
        opis.style.fontSize = "16px";
        opis.style.fontStyle = "italic";
        opis.style.margin = "0 auto 40px";
        opis.style.color = "#555";
        container.appendChild(opis);
      }

      pitanja
        .sort((a, b) => a.pozicija - b.pozicija)
        .forEach((pitanje, index) => {
          const questionDiv = document.createElement("div");
          questionDiv.style.marginBottom = "30px";

          const questionText = document.createElement("p");
          questionText.innerHTML = `<strong>${index + 1}. ${
            pitanje.tekstPitanja
          }</strong>`;
          questionText.style.marginBottom = "6px";
          questionDiv.appendChild(questionText);

          if (pitanje.objasnjenje) {
            const opis = document.createElement("p");
            opis.textContent = pitanje.objasnjenje;
            opis.style.fontStyle = "italic";
            opis.style.marginBottom = "10px";
            opis.style.marginLeft = "20px";
            questionDiv.appendChild(opis);
          }

          if (pitanje.tip === "TEKSTUALNO") {
            const line1 = document.createElement("hr");
            const line2 = document.createElement("hr");
            line1.style.border = "1px solid #000";
            line2.style.border = "1px solid #000";
            line1.style.width = "90%";
            line1.style.margin = "25px auto";
            line2.style.width = "90%";
            line2.style.margin = "25px auto";
            questionDiv.appendChild(line1);
            questionDiv.appendChild(line2);
          } else {
            const lista = document.createElement("ul");
            lista.style.listStyle = "none";
            lista.style.paddingLeft = "20px";

            pitanje.ponudjeniOdgovori.forEach((odg) => {
              const li = document.createElement("li");
              li.style.marginBottom = "12px";

              if (pitanje.tip === "VISESTRUKI_IZBOR") {
                li.innerHTML = `<input type="checkbox" disabled style="margin-right:8px; vertical-align: middle;"/> <span style="vertical-align: middle;">${odg.tekstOdgovora}</span>`;
              } else if (
                pitanje.tip === "JEDAN_IZBOR" ||
                pitanje.tip === "DA_NE"
              ) {
                li.innerHTML = `<input type="radio" disabled style="margin-right:8px; vertical-align: middle;"/> <span style="vertical-align: middle;">${odg.tekstOdgovora}</span>`;
              } else {
                li.textContent = odg.tekstOdgovora;
              }

              lista.appendChild(li);
            });
            questionDiv.appendChild(lista);
          }

          container.appendChild(questionDiv);
        });

      html2pdf()
        .from(container)
        .set({
          margin: 10,
          filename: `${anketa.naziv.replace(/\s+/g, "_")}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2 },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
        .save();
    } catch (error) {}
  };

  return (
    <Card
      title={<Title level={4}>Podešavanja ankete</Title>}
      bordered
      className="anketa-settings-card"
    >
      <Space
        wrap
        size="middle"
        style={{ display: "flex", justifyContent: "center", width: "100%" }}
      >
        <Button
          onClick={() => handleExportPDF(anketaId)}
          type="primary"
          icon={<FilePdfOutlined />}
        >
          Eksportuj anketu kao PDF
        </Button>

        <Button
          type="default"
          style={{ width: "200px" }}
          icon={<EditFilled />}
          onClick={(e) => {
            e.stopPropagation();
            onUpdateClick(anketaId);
          }}
        >
          Uredi
        </Button>
      </Space>

      <div className="status-Form">
        {token && (
          <div className="token-container">
            <Text strong>Token ankete:</Text>
            <Space direction="horizontal" style={{ marginTop: 8 }}>
              <input
                ref={tokenRef}
                value={token}
                readOnly
                onClick={() => tokenRef.current.select()}
                className="token-input"
              />
              <Tooltip title="Kopiraj token">
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => {
                    tokenRef.current.select();
                    navigator.clipboard.writeText(token).then(() => {
                      alert("Token je kopiran!");
                    });
                  }}
                />
              </Tooltip>
            </Space>
          </div>
        )}

        {anketa && (
          <div className="anketa-status-datum-wrapper">
            <div className="anketa-status-row">
              <label className="anketa-status-label">Link za pristup:</label>
              <Tooltip title="Kopiraj link ankete">
                <Button
                  type="primary"
                  onClick={() => {
                    const anketaLink = `${window.location.origin}/ankete/${anketaId}`;
                    navigator.clipboard.writeText(anketaLink).then(() => {
                      alert("Link ankete je kopiran!");
                    });
                  }}
                >
                  Kopiraj link
                </Button>
              </Tooltip>
            </div>

            <div className="anketa-status-row">
              <label className="anketa-status-label">Status ankete:</label>
              <Switch
                checked={anketa.statusAnkete === "AKTIVNA"}
                onChange={handleStatusToggle}
                checkedChildren="Aktivna"
                unCheckedChildren="Neaktivna"
                style={{ width: "100px", height: "25px" }}
              />
            </div>

            <div className="anketa-status-row">
              <label className="anketa-datum-label">Promjena datuma:</label>
              <DatePicker
                suffixIcon={<CalendarOutlined />}
                value={anketa.datumVazenja ? dayjs(anketa.datumVazenja) : null}
                onChange={handleDateChange}
                showTime
                style={{ width: "200px" }}
                placeholder="Izaberite datum"
              />
            </div>
          </div>
        )}
      </div>
      <Modal
        open={confirmVisible}
        title="Potvrda"
        onOk={confirmStatusChange}
        onCancel={() => setConfirmVisible(false)}
        confirmLoading={loading}
      >
        <p>Da li ste sigurni da želite da status postavite na "{newStatus}"?</p>
      </Modal>
      <Modal
        open={dateConfirmVisible}
        title="Potvrda"
        onOk={confirmDateChange}
        onCancel={cancelDateChange}
        okText="OK"
        cancelText="Otkaži"
      >
        <p>
          Da li ste sigurni da želite da datum važenja postavite na "
          {newDatumVazenja
            ? dayjs(newDatumVazenja).format("YYYY-MM-DD")
            : "Nema datuma"}
          "?
        </p>
      </Modal>
    </Card>
  );
};

export default AnketaSettings;
